/**
 * FamilyFeed — consolidated Board/Dashboard/Stories page at /board.
 *
 * Sections:
 *  1. Welcome header
 *  2. Share bar
 *  3. Quick-action tiles
 *  4. Wisdom moments strip (fixed count, "View all" to /wisdom)
 *  5. Search + filters
 *  6. Story grid (paginated)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDailyQuestion } from '../hooks/useDailyQuestion';
import DailyQuestionModal from '../components/chapters/DailyQuestionModal';
import {
  Search, Sparkles, BookOpen, Camera, Mic, ChefHat,
  ArrowRight, MessageCircle, SlidersHorizontal, ChevronRight,
} from 'lucide-react';
import HubLayout from '../components/common/HubLayout';
import { axiosInstance } from '../services/api';
import { getUser } from '../utils/secureStorage';
import { getWisdomMoments } from '../services/videoActivationAPI';

const FONT_H = "'Lora', Georgia, serif";
const FONT_B = "'Source Sans 3', sans-serif";
const AVATAR_COLORS = ['#556B2F', '#8E6B23', '#3B72C4', '#6B3FA0', '#E65100', '#7B6B8E'];
const avatarColor = (name) => AVATAR_COLORS[(name || '').charCodeAt(0) % AVATAR_COLORS.length];

export default function FamilyFeed() {
  const navigate = useNavigate();
  const userData = getUser() || {};
  const firstName = userData.first_name || 'there';

  // Daily question
  const { questionData, showModal: showDailyModal, handleAnswer, handleSkip, dismissModal, loading: dailyLoading } = useDailyQuestion();
  const [promptModalOpen, setPromptModalOpen] = useState(false);

  // Data
  const [stories, setStories] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [wisdomMoments, setWisdomMoments] = useState([]);

  // Filters
  const [searchText, setSearchText] = useState('');
  const [filterPerson, setFilterPerson] = useState(null);
  const [sort, setSort] = useState('newest');
  const [uploaders, setUploaders] = useState([]);

  const buildParams = useCallback((cursorOverride) => {
    const params = new URLSearchParams({ limit: '20', sort });
    if (cursorOverride) params.set('cursor', cursorOverride);
    if (filterPerson) params.set('person_id', filterPerson);
    if (searchText.trim()) params.set('q', searchText.trim());
    return params;
  }, [filterPerson, searchText, sort]);

  const fetchStories = useCallback(async (append = false, cursorOverride = null) => {
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const params = buildParams(cursorOverride);
      const r = await axiosInstance.get(`/lifeevent/feed/?${params}`);
      if (append) {
        setStories(prev => [...prev, ...r.data.items]);
      } else {
        setStories(r.data.items);
      }
      setCursor(r.data.nextCursor);
      setTotal(r.data.total);
    } catch { /* ignore */ }
    setLoading(false);
    setLoadingMore(false);
  }, [buildParams]);

  // Load wisdom moments (fixed count)
  useEffect(() => {
    getWisdomMoments({}).then(data => {
      setWisdomMoments((data.moments || []).slice(0, 4));
    }).catch(() => {});
    // Load family members for person filter
    axiosInstance.get('/genealogy/people/my_family_grouped/').then(res => {
      const d = res.data || {};
      const members = [
        ...(d.spouse ? [d.spouse] : []),
        ...(d.children || []),
        ...(d.parents || []),
        ...(d.siblings || []),
        ...(d.other || []),
      ];
      setUploaders(members);
    }).catch(() => {});
  }, []);

  // Fetch on filter change
  useEffect(() => { fetchStories(); }, [fetchStories]);

  const loadMore = () => { if (cursor) fetchStories(true, cursor); };

  return (
    <HubLayout activeView="hub">
      <div className="ff-content" style={{ maxWidth: 960, margin: '0 auto', padding: '32px 32px 80px' }}>

        {/* §1: Welcome header */}
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 28, color: '#2C2F26', margin: '0 0 4px' }}>
            Welcome back, {firstName}
          </h1>
          <p style={{ fontSize: 15, color: '#828A76', fontFamily: FONT_B, margin: 0 }}>
            Here's what's happening with your family
          </p>
        </div>

        {/* §2: Share bar */}
        <div style={{
          background: '#fff', border: '1px solid #EDEBDD', borderRadius: 16,
          padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14,
          boxShadow: '0 4px 16px rgba(40,46,28,.05)', marginBottom: 24,
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%', background: '#556B2F',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <BookOpen size={18} color="#fff" />
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ fontFamily: FONT_B, fontWeight: 700, fontSize: 16, color: '#2C2F26', margin: 0 }}>
              Share Something New
            </p>
            <p style={{ fontSize: 13, color: '#828A76', fontFamily: FONT_B, margin: 0 }}>
              A memory, a recipe, a photo — anything worth keeping
            </p>
          </div>
          <button onClick={() => navigate('/tell-a-story')} style={{
            background: '#556B2F', color: '#fff', fontWeight: 700, fontSize: 14,
            padding: '10px 20px', borderRadius: 999, border: 'none', cursor: 'pointer',
            fontFamily: FONT_B, whiteSpace: 'nowrap',
          }}>Share a Story</button>
        </div>

        {/* §3: Quick-action tiles */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 12, marginBottom: 28,
        }}>
          {[
            { icon: MessageCircle, label: 'Answer a Prompt', color: '#E65100', bg: '#FDEEE4', action: () => setPromptModalOpen(true) },
            { icon: Camera, label: 'Share Photos', color: '#556B2F', bg: '#EDF2E1', path: '/tell-a-story' },
            { icon: Mic, label: 'Record Your Voice', color: '#3B72C4', bg: '#EAF1FB', path: '/tell-a-story' },
            { icon: ChefHat, label: 'Share a Recipe', color: '#8E6B23', bg: '#FDF1E4', path: '/tell-a-story' },
          ].map((tile, i) => (
            <button key={i} onClick={() => tile.action ? tile.action() : navigate(tile.path)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: '#fff', border: '1px solid #EDEBDD', borderRadius: 12,
                padding: '14px 16px', cursor: 'pointer', textAlign: 'left',
              }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(40,46,28,.08)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
            >
              <div style={{
                width: 36, height: 36, borderRadius: 10, background: tile.bg,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <tile.icon size={18} color={tile.color} />
              </div>
              <span style={{ fontFamily: FONT_B, fontWeight: 600, fontSize: 14, color: '#2C2F26' }}>
                {tile.label}
              </span>
            </button>
          ))}
        </div>

        {/* §4: Wisdom moments strip */}
        {wisdomMoments.length > 0 && (
          <div style={{ marginBottom: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Sparkles size={18} color="#556B2F" />
                <h2 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 20, color: '#2C2F26', margin: 0 }}>
                  Family Wisdom
                </h2>
              </div>
              <button onClick={() => navigate('/wisdom')} style={{
                background: 'none', border: 'none', color: '#556B2F', fontWeight: 700,
                fontSize: 14, cursor: 'pointer', fontFamily: FONT_B,
                display: 'flex', alignItems: 'center', gap: 4,
              }}>View all <ArrowRight size={14} /></button>
            </div>
            <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 4 }}>
              {wisdomMoments.map(m => (
                <div key={m.id} onClick={() => navigate('/wisdom')} style={{
                  background: '#fff', border: '1px solid #EDEBDD', borderRadius: 14,
                  padding: '14px 16px', minWidth: 200, maxWidth: 260, cursor: 'pointer',
                  flexShrink: 0,
                }}>
                  <p style={{
                    fontFamily: FONT_H, fontStyle: 'italic', fontSize: 15,
                    color: '#2C2F26', margin: '0 0 8px', lineHeight: 1.4,
                    display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                  }}>
                    "{m.original_text?.substring(0, 100) || m.summary || ''}"
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{
                      width: 22, height: 22, borderRadius: '50%',
                      background: avatarColor(m.speaker?.name || ''),
                      color: '#fff', fontSize: 10, fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>{(m.speaker?.name || '?')[0].toUpperCase()}</div>
                    <span style={{ fontSize: 12, color: '#828A76', fontFamily: FONT_B }}>
                      {m.speaker?.name || 'Unknown'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* §5: Search + filters */}
        <div style={{
          display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 200,
            background: '#fff', border: '1.5px solid #DCD9C8', borderRadius: 12,
            padding: '9px 14px',
          }}>
            <Search size={16} color="#9AA088" />
            <input value={searchText} onChange={e => setSearchText(e.target.value)}
              placeholder="Search stories..."
              style={{
                border: 'none', outline: 'none', background: 'transparent',
                fontSize: 15, fontFamily: FONT_B, color: '#2C2F26', flex: 1,
              }}
            />
          </div>

          {/* Person filter */}
          <select value={filterPerson || ''} onChange={e => setFilterPerson(e.target.value || null)}
            style={{
              background: '#fff', border: '1.5px solid #DCD9C8', borderRadius: 12,
              padding: '9px 14px', fontSize: 14, fontFamily: FONT_B, color: '#2C2F26',
              cursor: 'pointer', minHeight: 40,
            }}>
            <option value="">All people</option>
            {uploaders.map(u => (
              <option key={u.id} value={u.id}>{u.first_name || u.name}</option>
            ))}
          </select>

          {/* Sort */}
          <select value={sort} onChange={e => setSort(e.target.value)}
            style={{
              background: '#fff', border: '1.5px solid #DCD9C8', borderRadius: 12,
              padding: '9px 14px', fontSize: 14, fontFamily: FONT_B, color: '#2C2F26',
              cursor: 'pointer', minHeight: 40,
            }}>
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
          </select>
        </div>

        {/* §6: Story grid */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#828A76', fontFamily: FONT_B }}>
            Loading stories...
          </div>
        ) : stories.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: 40, background: '#fff',
            borderRadius: 16, border: '1px solid #E4E1D2',
          }}>
            <BookOpen size={32} color="#B9C3A0" style={{ marginBottom: 10 }} />
            <p style={{ fontFamily: FONT_H, fontSize: 18, fontWeight: 600, color: '#2C2F26', margin: '0 0 6px' }}>
              No stories yet
            </p>
            <p style={{ color: '#6E7562', fontSize: 15, margin: 0, fontFamily: FONT_B }}>
              Be the first to share a story with your family.
            </p>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#9AA088', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 12, fontFamily: FONT_B }}>
              {total} {total === 1 ? 'story' : 'stories'}
            </div>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 16,
            }}>
              {stories.map(story => (
                <div key={story.id}
                  onClick={() => navigate(`/stories/${story.id}`)}
                  style={{
                    background: '#fff', borderRadius: 16, border: '1px solid #EDEBDD',
                    overflow: 'hidden', cursor: 'pointer', transition: 'box-shadow 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = '0 6px 20px rgba(40,46,28,.08)'}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                >
                  {/* Cover image */}
                  <div style={{
                    height: 160, background: '#EFEEE5',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden',
                  }}>
                    {story.cover_url ? (
                      <img src={story.cover_url} alt="" style={{
                        width: '100%', height: '100%', objectFit: 'cover',
                      }} loading="lazy" />
                    ) : (
                      <BookOpen size={28} color="#C4CFA8" />
                    )}
                  </div>
                  {/* Card body */}
                  <div style={{ padding: '14px 16px' }}>
                    {/* Subject label — only when story is about a child */}
                    {story.subject && (
                      <p style={{
                        fontFamily: FONT_B, fontWeight: 700, fontSize: 12,
                        letterSpacing: '1.1px', textTransform: 'uppercase',
                        color: '#556B2F', margin: '0 0 6px',
                      }}>
                        About {story.subject.firstName}
                      </p>
                    )}
                    <h3 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 17, color: '#2C2F26', margin: '0 0 6px', lineHeight: 1.3 }}>
                      {story.title}
                    </h3>
                    {story.story && (
                      <p style={{ fontSize: 13, color: '#6E7562', fontFamily: FONT_B, margin: '0 0 10px', lineHeight: 1.4 }}>
                        {story.story}
                      </p>
                    )}
                    {/* Byline — below description */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                      <div style={{
                        width: 26, height: 26, borderRadius: '50%',
                        background: '#3E4F22',
                        color: '#fff', fontSize: 12, fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>{story.author?.initial || '?'}</div>
                      <span style={{ fontSize: 14, color: '#828A76', fontFamily: FONT_B }}>
                        {story.subject ? 'Added by ' : ''}<span style={story.subject ? { fontWeight: 600, color: '#5A6150' } : {}}>{story.author?.name}</span> &middot; {story.date ? new Date(story.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                      </span>
                    </div>
                    <button style={{
                      background: '#F0F5E8', border: '1px solid #C8D5B0', borderRadius: 8,
                      padding: '6px 12px', fontSize: 13, fontWeight: 600, color: '#556B2F',
                      cursor: 'pointer', fontFamily: FONT_B,
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}>
                      View & Comment <ChevronRight size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Load more */}
            {cursor && (
              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <button onClick={loadMore} disabled={loadingMore} style={{
                  background: '#556B2F', color: '#fff', fontWeight: 700, fontSize: 15,
                  padding: '12px 28px', borderRadius: 999, border: 'none',
                  cursor: loadingMore ? 'wait' : 'pointer', fontFamily: FONT_B,
                  opacity: loadingMore ? 0.7 : 1, minHeight: 44,
                }}>
                  {loadingMore ? 'Loading...' : 'Load more stories'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Daily Question Modal */}
      {(promptModalOpen || showDailyModal) && questionData?.has_question && (
        <DailyQuestionModal
          questionData={questionData}
          onAnswer={async (data, files) => {
            await handleAnswer(data, files);
            setPromptModalOpen(false);
            fetchStories(); // refresh feed after answering
          }}
          onSkip={() => {
            handleSkip();
            setPromptModalOpen(false);
          }}
          onDismiss={() => {
            dismissModal();
            setPromptModalOpen(false);
          }}
        />
      )}

      <style>{`
        @media (max-width: 640px) {
          .ff-content { padding: 16px 16px 60px !important; }
        }
      `}</style>
    </HubLayout>
  );
}
